
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Copy, Upload, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";

export const CopyFilesToRoot = () => {
  const [isCopying, setIsCopying] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [lastCopied, setLastCopied] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);
  const { toast } = useToast();

  const verifySitemapAccess = async (): Promise<{ success: boolean; message: string; details?: any }> => {
    try {
      // Test direct access to the sitemap with cache busting
      const testUrl = `https://elfletterbot.com/sitemap.xml?verify=${Date.now()}`;
      console.log('Verifying sitemap access at:', testUrl);
      
      const response = await fetch(testUrl, {
        method: 'HEAD', // Just check headers
        cache: 'no-cache'
      });
      
      const details = {
        status: response.status,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length'),
        cacheControl: response.headers.get('cache-control'),
        sitemapSource: response.headers.get('x-sitemap-source'),
        servedAt: response.headers.get('x-served-at')
      };
      
      if (response.ok) {
        return {
          success: true,
          message: `Sitemap is accessible! Status: ${response.status}`,
          details
        };
      } else {
        return {
          success: false,
          message: `Sitemap not accessible. Status: ${response.status}`,
          details
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `Verification failed: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  };

  const copyFilesToRoot = async () => {
    try {
      setIsCopying(true);
      setVerificationStatus(null);
      
      toast({
        title: "Sitemap Refresh",
        description: "Sitemap and robots.txt are generated as static root files.",
        variant: "default",
      });

      console.log('Sitemap and robots.txt are generated as static root files.');
      setLastCopied(new Date().toISOString());
      
      toast({
        title: "Files Copied Successfully",
        description: "Verifying access to domain root...",
        variant: "default",
      });
      
      // Wait a moment for files to propagate
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Verify the files are accessible
      setIsVerifying(true);
      const verification = await verifySitemapAccess();
      setVerificationStatus(verification);
      
      if (verification.success) {
        toast({
          title: "Verification Successful",
          description: "Sitemap is now accessible at domain root",
          variant: "default",
        });
      } else {
        toast({
          title: "Verification Warning",
          description: verification.message,
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error("Error copying files to domain root:", error);
      
      toast({
        title: "Copy Failed",
        description: `Failed to copy files: ${error instanceof Error ? error.message : String(error)}`,
        variant: "destructive",
      });
    } finally {
      setIsCopying(false);
      setIsVerifying(false);
    }
  };

  const testSitemapAccess = async () => {
    setIsVerifying(true);
    const verification = await verifySitemapAccess();
    setVerificationStatus(verification);
    setIsVerifying(false);
    
    toast({
      title: verification.success ? "Access Test Successful" : "Access Test Failed",
      description: verification.message,
      variant: verification.success ? "default" : "destructive",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Copy className="h-5 w-5" />
          Copy Files to Domain Root
        </CardTitle>
        <CardDescription>
          Sitemap and robots.txt are generated as static root files during the build.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button 
            onClick={copyFilesToRoot} 
            disabled={isCopying || isVerifying}
            className="flex-1"
          >
            {isCopying ? (
              <>
                <Upload className="mr-2 h-4 w-4 animate-pulse" />
                Copying Files...
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy to Domain Root
              </>
            )}
          </Button>
          
          <Button 
            variant="outline"
            onClick={testSitemapAccess}
            disabled={isCopying || isVerifying}
          >
            {isVerifying ? (
              <>
                <AlertCircle className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <ExternalLink className="mr-2 h-4 w-4" />
                Test Access
              </>
            )}
          </Button>
        </div>

        {lastCopied && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" />
            Last copied: {new Date(lastCopied).toLocaleString()}
          </div>
        )}

        {verificationStatus && (
          <div className={`p-3 rounded-lg border ${verificationStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              {verificationStatus.success ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
              <span className={`font-medium text-sm ${verificationStatus.success ? 'text-green-700' : 'text-red-700'}`}>
                {verificationStatus.message}
              </span>
            </div>
            
            {verificationStatus.details && (
              <div className="text-xs text-gray-600 space-y-1">
                <div>Status: {verificationStatus.details.status}</div>
                <div>Content-Type: {verificationStatus.details.contentType || 'N/A'}</div>
                <div>Content-Length: {verificationStatus.details.contentLength || 'N/A'} bytes</div>
                <div>Source: {verificationStatus.details.sitemapSource || 'Unknown'}</div>
                {verificationStatus.details.servedAt && (
                  <div>Served at: {new Date(verificationStatus.details.servedAt).toLocaleString()}</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p>This action copies your generated sitemap.xml and robots.txt files into your Cloudflare-served site assets so they are accessible at:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>https://elfletterbot.com/sitemap.xml</li>
            <li>https://elfletterbot.com/robots.txt</li>
          </ul>
          <p className="mt-2">Use "Test Access" to verify the sitemap is accessible at the domain root after copying.</p>
        </div>
      </CardContent>
    </Card>
  );
};
